import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/api';
import type { Database } from '@/types/database.generated';

export type SmsLogRow = Database['public']['Tables']['sms_logs']['Row'];
export type SmsCreditTxnRow = Database['public']['Tables']['sms_credit_transactions']['Row'];
export type SmsLogStatus = Database['public']['Enums']['sms_log_status'];
export type SmsTriggerType = Database['public']['Enums']['sms_trigger_type'];
export type SmsCreditTxnKind = Database['public']['Enums']['sms_credit_txn_kind'];

export interface SmsCreditState {
  balance: number;
  lifetimePurchased: number;
  lifetimeUsed: number;
  lifetimePurchasedSegments: number;
  lifetimeUsedSegments: number;
  lifetimeUsedMessages: number;
  lowBalanceThreshold: number;
}

export interface SmsClinicSettings {
  enabled?: boolean;
  sender?: string;
  defaultCountryCode?: string;
  dailyCap?: number;
  quietHours?: { startMinutes?: number; endMinutes?: number; timezone?: string } | null;
}

export interface SmsClinicState {
  credits: SmsCreditState;
  settings: SmsClinicSettings;
}

export const smsClinicQueryKey = (clinicId: string | null | undefined) =>
  ['sms', 'clinic', clinicId ?? null] as const;

export const smsLogsQueryKey = (clinicId: string | null | undefined) =>
  ['sms', 'logs', clinicId ?? null] as const;

export const patientSmsLogsQueryKey = (
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) => ['sms', 'logs', clinicId ?? null, 'patient', patientId ?? null] as const;

export const smsCreditTxnsQueryKey = (clinicId: string | null | undefined) =>
  ['sms', 'credit-txns', clinicId ?? null] as const;

function readCredits(raw: unknown): SmsCreditState {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    balance: Number(o.balance ?? 0),
    lifetimePurchased: Number(o.lifetimePurchased ?? o.lifetime_purchased ?? 0),
    lifetimeUsed: Number(o.lifetimeUsed ?? o.lifetime_used ?? 0),
    lifetimePurchasedSegments: Number(o.lifetimePurchasedSegments ?? 0),
    lifetimeUsedSegments: Number(o.lifetimeUsedSegments ?? 0),
    lifetimeUsedMessages: Number(o.lifetimeUsedMessages ?? 0),
    lowBalanceThreshold: Number(o.lowBalanceThreshold ?? o.low_balance_threshold ?? 0),
  };
}

function readSettings(raw: unknown): SmsClinicSettings {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    enabled: Boolean(o.enabled),
    sender: typeof o.sender === 'string' ? o.sender : undefined,
    defaultCountryCode: typeof o.defaultCountryCode === 'string' ? o.defaultCountryCode : undefined,
    dailyCap: typeof o.dailyCap === 'number' ? o.dailyCap : undefined,
    quietHours: (o.quietHours as SmsClinicSettings['quietHours']) ?? null,
  };
}

export function useSmsClinic(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: smsClinicQueryKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<SmsClinicState> => {
      if (!clinicId) throw new Error('No clinic id');
      const { data, error } = await supabase
        .from('clinics')
        .select('sms_credits, sms_settings')
        .eq('id', clinicId)
        .single();
      if (error) throw error;
      const credits = readCredits(data.sms_credits);
      const { data: usage } = await supabase.rpc('get_clinic_sms_usage', {
        p_clinic_id: clinicId,
      });
      const u = usage && typeof usage === 'object' ? (usage as Record<string, unknown>) : {};
      return {
        credits: {
          ...credits,
          lifetimeUsedMessages: Number(u.lifetime_used_messages ?? credits.lifetimeUsedMessages),
          lifetimeUsedSegments: Number(u.lifetime_used_segments ?? credits.lifetimeUsedSegments),
          lifetimeUsed: Number(u.lifetime_used_cents ?? credits.lifetimeUsed),
          lifetimePurchased: Number(u.lifetime_purchased_cents ?? credits.lifetimePurchased),
          lifetimePurchasedSegments: Number(
            u.lifetime_purchased_segments ?? credits.lifetimePurchasedSegments,
          ),
        },
        settings: readSettings(data.sms_settings),
      };
    },
    staleTime: 5_000,
  });
}

export interface SmsLogsFilters {
  status?: SmsLogStatus | 'all';
  trigger?: SmsTriggerType | 'all';
  search?: string;
  limit?: number;
}

export function useSmsLogs(clinicId: string | null | undefined, filters: SmsLogsFilters = {}) {
  return useQuery({
    queryKey: [...smsLogsQueryKey(clinicId), filters] as const,
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<SmsLogRow[]> => {
      if (!clinicId) return [];
      let q = supabase
        .from('sms_logs')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: false })
        .limit(filters.limit ?? 200);

      if (filters.status && filters.status !== 'all') {
        q = q.eq('status', filters.status);
      }
      if (filters.trigger && filters.trigger !== 'all') {
        q = q.eq('trigger', filters.trigger);
      }
      if (filters.search?.trim()) {
        const pattern = `%${filters.search.trim()}%`;
        q = q.or(
          `to_phone.ilike.${pattern},body.ilike.${pattern},patient_name_snapshot.ilike.${pattern}`,
        );
      }

      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 10_000,
  });
}

/**
 * Query SMS logs for a specific patient.
 */
export function usePatientSmsLogs(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
  limit: number = 100,
) {
  return useQuery({
    queryKey: [...patientSmsLogsQueryKey(clinicId, patientId), limit] as const,
    enabled: Boolean(clinicId) && Boolean(patientId),
    queryFn: async (): Promise<SmsLogRow[]> => {
      if (!clinicId || !patientId) return [];
      const { data, error } = await supabase
        .from('sms_logs')
        .select('*')
        .eq('clinic_id', clinicId)
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 10_000,
  });
}

export function useSmsCreditTransactions(clinicId: string | null | undefined, limit: number = 50) {
  return useQuery({
    queryKey: [...smsCreditTxnsQueryKey(clinicId), limit] as const,
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<SmsCreditTxnRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('sms_credit_transactions')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 10_000,
  });
}

// ---- Mutations ----

export interface SendSmsInput {
  clinic_id: string;
  body: string;
  trigger: Extract<SmsTriggerType, 'manual' | 'consent_request' | 'test'>;
  patient_id?: string;
  subscriber_identifier?: string;
  appointment_id?: string;
  patient_name?: string;
  idempotency_key?: string;
  override_sender?: string;
  [key: string]: unknown;
}

export interface SendSmsOutput {
  sms_log_id: string;
  provider_message_id: string | null;
  status: 'queued' | 'sent';
  parts: number;
  clinic_price_cents: number;
  balance_after_cents: number | null;
}

export function useSendSms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendSmsInput) =>
      callFunction<SendSmsInput, SendSmsOutput>('send-sms', input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: smsClinicQueryKey(vars.clinic_id) });
      qc.invalidateQueries({ queryKey: smsLogsQueryKey(vars.clinic_id) });
      qc.invalidateQueries({ queryKey: smsCreditTxnsQueryKey(vars.clinic_id) });
      // Also invalidate patient-specific logs if patient_id is present
      if (vars.patient_id) {
        qc.invalidateQueries({
          queryKey: patientSmsLogsQueryKey(vars.clinic_id, vars.patient_id),
        });
      }
    },
  });
}

export interface TopUpSmsInput {
  clinic_id: string;
  amount_cents: number;
  reason: string;
  [key: string]: unknown;
}

export interface TopUpSmsOutput {
  clinic_id: string;
  balance_after_cents: number;
}

export function useTopUpSmsCredits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TopUpSmsInput) =>
      callFunction<TopUpSmsInput, TopUpSmsOutput>('top-up-sms-credits', input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: smsClinicQueryKey(vars.clinic_id) });
      qc.invalidateQueries({ queryKey: smsCreditTxnsQueryKey(vars.clinic_id) });
    },
  });
}

// ---- Realtime: sms_logs live DLR updates ------------------------------------

/**
 * Subscribe to Postgres changes on sms_logs for the given clinic.
 * On any UPDATE, invalidates the sms_logs TanStack Query cache so the
 * delivery-status (DLR) reflects in the table without a page refresh.
 *
 * MOD-9b sprint 1 spec: "Realtime DLR push to UI".
 */
export function useSmsLogsRealtime(clinicId: string | null | undefined) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!clinicId) return;

    const channel = supabase
      .channel(`sms_logs:clinic-${clinicId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'sms_logs',
          filter: `clinic_id=eq.${clinicId}`,
        },
        () => {
          // Invalidate so the query refetches with fresh status
          qc.invalidateQueries({ queryKey: smsLogsQueryKey(clinicId) });
          // Also invalidate all patient-specific queries
          qc.invalidateQueries({ queryKey: ['sms', 'logs', clinicId, 'patient'] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clinicId, qc]);
}

/**
 * Subscribe to Postgres changes on sms_logs for a specific patient.
 * Keeps the patient SMS tab real-time.
 */
export function usePatientSmsLogsRealtime(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!clinicId || !patientId) return;

    const channel = supabase
      .channel(`sms_logs:patient-${patientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sms_logs',
          filter: `patient_id=eq.${patientId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: patientSmsLogsQueryKey(clinicId, patientId) });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clinicId, patientId, qc]);
}

// ---- Appointment SMS jobs ---------------------------------------------------

export interface AppointmentSmsJobRow {
  id: string;
  clinic_id: string;
  appointment_id: string;
  trigger: 'confirmation' | 'reminder' | 'followup';
  status: 'pending' | 'sent' | 'skipped' | 'failed';
  scheduled_for: string | null;
  attempted_at: string | null;
  sms_log_id: string | null;
  reason: string | null;
  created_at: string;
  updated_at: string;
}

export const apptSmsJobsQueryKey = (clinicId: string | null | undefined) =>
  ['sms', 'appt-jobs', clinicId ?? null] as const;

export function useAppointmentSmsJobs(clinicId: string | null | undefined, limit: number = 100) {
  return useQuery({
    queryKey: [...apptSmsJobsQueryKey(clinicId), limit] as const,
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<AppointmentSmsJobRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('appointment_sms_jobs')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as AppointmentSmsJobRow[];
    },
    staleTime: 15_000,
  });
}

// ---- Formatting helpers ----

export function formatBalance(cents: number, currency: string = 'DKK'): string {
  const v = (cents / 100).toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${v} ${currency}`;
}
